import { useCallback, useEffect, useState } from "react";
import { team } from "../api";
import type { MintedInvite, TeamInvite, TeamMember } from "../types";

/** The grantable roles. One entry today; the picker is data-driven so adding a
 *  role later (once Guardius defines it) is a one-line change, not a rewrite. */
const ROLES = ["member"] as const;
type Role = (typeof ROLES)[number];

const DEFAULT_EXPIRY_DAYS = 7;

/** An ISO 8601 timestamp `days` from now — the default invite expiry. */
function expiryFromNow(days: number): string {
  return new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();
}

function truncate(addr: string): string {
  return addr.length <= 13 ? addr : `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

export function MembersPanel({
  canInvite,
  canManageTeam,
}: {
  canInvite: boolean;
  canManageTeam: boolean;
}) {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [invites, setInvites] = useState<TeamInvite[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [role, setRole] = useState<Role>("member");
  const [maxUses, setMaxUses] = useState(1);
  const [expiresAt, setExpiresAt] = useState(() => expiryFromNow(DEFAULT_EXPIRY_DAYS));
  const [label, setLabel] = useState("");
  const [minting, setMinting] = useState(false);
  const [minted, setMinted] = useState<MintedInvite | null>(null);
  const [copied, setCopied] = useState(false);

  const refresh = useCallback(() => {
    if (canInvite) {
      team
        .members()
        .then((r) => setMembers(r.members))
        .catch((e: unknown) => setError(String(e)));
    }
    if (canManageTeam) {
      team
        .invites()
        .then((r) => setInvites((r.invites ?? []).filter((i) => i.status !== "revoked")))
        .catch((e: unknown) => setError(String(e)));
    }
  }, [canInvite, canManageTeam]);

  useEffect(() => refresh(), [refresh]);

  const shareUrl = minted
    ? `${window.location.origin}${import.meta.env.BASE_URL}?invite=${minted.code}`
    : "";

  const onMint = async (e: React.FormEvent) => {
    e.preventDefault();
    if (minting) return;
    setMinting(true);
    setError(null);
    setCopied(false);
    try {
      const result = await team.mintInvite({
        role,
        maxUses,
        expiresAt,
        ...(label.trim() ? { label: label.trim() } : {}),
      });
      setMinted(result);
      refresh();
    } catch (err) {
      setError(String(err));
    } finally {
      setMinting(false);
    }
  };

  const onRevoke = async (inviteId: string) => {
    try {
      await team.revokeInvite(inviteId);
      refresh();
    } catch (err) {
      setError(String(err));
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="members-panel">
      {error && (
        <p className="members-error" role="alert">
          {error}
        </p>
      )}

      {canInvite && (
        <section className="members-section">
        <h3>Create invite</h3>
        <form className="members-mint" onSubmit={onMint}>
          <label>
            Role
            <select value={role} onChange={(e) => setRole(e.target.value as Role)} aria-label="Role">
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </label>
          <label>
            Label (optional)
            <input value={label} onChange={(e) => setLabel(e.target.value)} aria-label="Label" />
          </label>
          <label>
            Expires at (ISO 8601)
            <input value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} aria-label="Expires at" />
          </label>
          <label>
            Max uses
            <input
              type="number"
              min={1}
              value={maxUses}
              onChange={(e) => setMaxUses(Math.max(1, Number(e.target.value) || 1))}
              aria-label="Max uses"
            />
          </label>
          <button type="submit" disabled={minting}>
            {minting ? "Creating…" : "Create invite link"}
          </button>
        </form>
        {minted && (
          <div className="members-minted" role="status">
            <p>Shown once — copy it now:</p>
            <code data-testid="invite-link">{shareUrl}</code>
            <button type="button" onClick={copyLink}>
              {copied ? "Copied!" : "Copy link"}
            </button>
          </div>
        )}
        </section>
      )}

      {canManageTeam && (
        <section className="members-section">
        <h3>Pending invites</h3>
        {invites.length === 0 ? (
          <p className="members-empty">No outstanding invites.</p>
        ) : (
          <table className="members-table">
            <thead>
              <tr>
                <th>Label</th>
                <th>Expires</th>
                <th>Uses</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {invites.map((inv) => (
                <tr key={inv.inviteId}>
                  <td>{inv.label ?? "—"}</td>
                  <td>{inv.expiresAt ? new Date(inv.expiresAt * 1000).toLocaleDateString() : "—"}</td>
                  <td>
                    {inv.useCount ?? 0} / {inv.maxUses ?? "∞"}
                  </td>
                  <td>
                    <button type="button" onClick={() => onRevoke(inv.inviteId)}>
                      Revoke
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        </section>
      )}

      {canInvite && (
        <section className="members-section">
        <h3>Members</h3>
        {members.length === 0 ? (
          <p className="members-empty">No members yet.</p>
        ) : (
          <table className="members-table">
            <thead>
              <tr>
                <th>Identity</th>
                <th>Role</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.identityId}>
                  <td title={m.walletAddress}>{truncate(m.walletAddress || m.identityId)}</td>
                  <td>{m.roles.length ? m.roles.join(", ") : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        </section>
      )}
    </div>
  );
}
